import { BulkOperationProps } from '@type/bulk-operations';
import { useBjForm } from '@hooks/useBjForm';
import { Trans, useTranslation } from 'next-i18next';
import { useSwrBulkSendCvsOnEmail } from '@hooks/useSwrApi/endpoints/bulk-operations';
import {
  Button, Input, Label, RadioText,
} from '@components/common/Forms';
import { LinkStyled, Modal } from '@components/common';
import { PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { useOpenBulkModal } from './hooks/useOpenBulkModal';
import { DynamicEmailList } from './DynamicEmailList';

interface BulkSendCvProps extends BulkOperationProps {
  hideTriggerIcon?: boolean;
  triggerDisabled?: boolean;
}

/**
 * @description The bulk send CV's to another people via email.
 *
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants (ie: interview, new, etc.).
 * - **jobSlug**: The job slug.
 * - **locale**: The locale
 * - **queryParams**: The filters query params object.
 */
export const BulkSendCv = (props: BulkSendCvProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    selectAll,
    status,
    queryParams,
    selectByColumn,
    normalizeSelectedApplicantsForBulkAction,
    hideTriggerIcon = false,
    triggerDisabled = false,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Handle the bulk applicants modal toggle and base post object build
  const {
    isOpen,
    toggleBulkModal,
    closeOnSuccess,
    basePostObject,
  } = useOpenBulkModal({
    getSelectedApplicants, selectAll, status, queryParams, selectByColumn, normalizeSelectedApplicantsForBulkAction,
  });


  // BJ Form
  const {
    handleCallbackSubmit,
    record,
    updateFormElements,
  } = useBjForm({
    wizardData: basePostObject || {},
  });


  // SWR: Send group message
  const {
    trigger,
    isMutating,
  } = useSwrBulkSendCvsOnEmail(jobSlug, locale, closeOnSuccess);


  // Render component
  // ********************************
  return (
    <>
      {/* TRIGGER BUTTON */}
      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        disabled={triggerDisabled}
        onClick={toggleBulkModal}
        aria-label={t('cv.action.send')}
      >
        {!hideTriggerIcon && <PaperAirplaneIcon className="mr-2 size-5 sm:mr-1" />}
        <span>{t('cv.action.send')}</span>
      </Button>


      {/* SEND CV's MODAL */}
      <Modal isOpen={isOpen} onClose={toggleBulkModal} size="sm">
        <form noValidate onSubmit={(event) => handleCallbackSubmit(event, trigger)}>

          {/* Modal header */}
          <Modal.Header onClose={toggleBulkModal}>
            {t('form.bulkcv.admin.title')}
          </Modal.Header>

          {/* Modal body */}
          <Modal.Body className="bg-surface-100 !p-0">
            <div className="p-6">
              {/* Description */}
              <h3 className="text-lg font-bold">{t('form.bulkcv.admin.desc.title')}</h3>
              <p className="my-3">{t('form.bulkcv.admin.desc1')}</p>
              <p className="mb-5 mt-3">{t('form.bulkcv.admin.desc2')}</p>

              {/* Email inputs */}
              <DynamicEmailList
                locale={locale}
                record={record}
                updateFormElements={updateFormElements}
              />
            </div>

            <div className="border-t border-input/40 bg-surface-50 p-6 text-ink-medium">
              {/* Send without invite */}
              <Label htmlFor="withoutInvite" className="!m-0 flex cursor-pointer items-start">
                <Input
                  type="checkbox"
                  className="mt-0.5"
                  {...record('withoutInvite', { convertValue: 'toBoolean' })}
                />
                <RadioText>{t('form.bulkcv.admin.without.invite.label')}</RadioText>
              </Label>

              {/* GDPR info */}
              <div className="mt-4 text-sm">
                <span className="mr-1 text-error">*</span>
                <Trans
                  i18nKey="form.bulkcv.admin.gdpr.info"
                  components={{
                    gdprTag: <LinkStyled href="/legal/confidentiality" styling="text" className="text-sm" />,
                  }}
                />
              </div>
            </div>

          </Modal.Body>

          {/* Modal footer */}
          <Modal.Footer className="text-right">
            <Button
              type="submit"
              color="secondary"
              rounding="full"
              size="sm"
              className="!px-7"
              isLoading={isMutating}
            >
              <PaperAirplaneIcon className="size-5 sm:mr-1" />
              {t('global.action.send')}
            </Button>
          </Modal.Footer>

        </form>
      </Modal>
    </>
  );
};
